<template>
  <div>
    <div class="medication-target-selects">
      <el-select
        v-close-on-zoom
        :value="state.check.target"
        :disabled="readonly"
        class="target-select"
        @input="value => updateTarget(value)"
      >
        <el-option value="CUMULATIVE" :label="'Was Cumulative'" />
        <el-option value="CURRENT" label="Is Most Recent" />
      </el-select>
      <medication-fluid-selector
        :selected="state.check.id"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :dosage-form-filter="
          dosageForm =>
            dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
        "
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentId) => {
            if (treatmentType === 'MEDICATION') {
              selectMedication(treatmentId);
            } else if (treatmentType === 'FLUID') {
              selectFluid(treatmentId);
            }
          }
        "
      />
      <help-tooltip v-if="medFluidError" type="error">
        <span>{{ medFluidError.message }}</span>
      </help-tooltip>
      <help-tooltip v-if="ivDisabledError" type="error">
        <span>{{ ivDisabledError.message }}</span>
      </help-tooltip>
      <md-field class="bag-volume-select-field">
        <infusion-bag-volume-select
          :value="state.check.bagVolumes"
          :disabled="readonly"
          :default-any="true"
          :available="isGlobal ? null : availableBagVolumes"
          @input="bagVolumeKeys => updateBagVolumes(bagVolumeKeys)"
        />
      </md-field>
      <help-tooltip v-if="bagVolumeError" type="error">
        <span>{{ bagVolumeError.message }}</span>
      </help-tooltip>
      <el-select
        v-close-on-zoom
        :value="state.check.metric"
        :disabled="readonly"
        class="metric-select"
        @input="value => updateMetric(value)"
      >
        <el-option v-if="state.check.target === 'CUMULATIVE'" value="GIVEN" :label="'given'" />
        <el-option value="DELIVERED" :label="'delivered'" />
      </el-select>
    </div>
    <div class="dosage-container">
      <dosage-count-check
        v-if="countAvailable"
        :disabled="readonly"
        mode="COUNT"
        prefix="with # of administrations"
        :dosage-available="false"
        :count-available="true"
        :count="state.check.count"
        :count-operator="state.check.countOperator"
        class="dosage-count-check"
        @updateCountOperator="updateCountOperator"
        @updateCount="updateCount"
      />
      <dosage-count-check
        v-if="!countAvailable"
        :disabled="readonly"
        mode="DOSAGE"
        prefix="with dosage between"
        :dosage-available="true"
        :count-available="false"
        :dosage="state.check.dosage"
        :units="state.check.unit"
        :units-available="availableUnits"
        class="dosage-count-check"
        @updateDosage="updateDosage"
        @updateUnits="updateUnit"
      />
      <help-tooltip v-if="unitDisabledError" type="error">
        <span>{{ unitDisabledError.message }}</span>
      </help-tooltip>
    </div>
    <el-card v-if="state.check.id" class="infusion-location-options-container">
      <el-select
        v-close-on-zoom
        :value="state.check.ivInfusionLocation"
        :disabled="readonly"
        placeholder="Any infusion location"
        class="iv-infusion-location-select"
        @input="value => updateIvInfusionLocation(value)"
      >
        <el-option value="IV_SITE" label="IV Site" />
        <el-option value="MEDICATION" label="Medication" />
        <el-option value="FLUID" label="Fluid" />
      </el-select>
      <iv-location-select
        v-if="state.check.ivInfusionLocation === 'IV_SITE'"
        :iv-location="state.check.ivInfusionLocationIv || {}"
        :case-conf="emitter.caseConf"
        :is-global="isGlobal"
        :readonly="readonly"
        :iv-category-error="ivCategoryError"
        :iv-port-error="ivPortError"
        :iv-site-error="ivSiteError"
        :iv-side-error="ivSideError"
        @update="updateIvInfusionLocationIv"
      />

      <medication-fluid-selector
        v-if="state.check.ivInfusionLocation === 'MEDICATION'"
        :multiple="true"
        :show-fluids="false"
        :selected="state.check.ivInfusionLocationMedications || []"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :dosage-form-filter="
          dosageForm =>
            dosageForm.enabledRoutes.find(r => r.key === 'IV_IO_INFUSION')
        "
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentIds) => {
            updateIvInfusionLocationMedications(treatmentIds);
          }
        "
      />
      <help-tooltip v-if="locationMedError" type="error">
        <span>{{ locationMedError.message }}</span>
      </help-tooltip>

      <medication-fluid-selector
        v-if="state.check.ivInfusionLocation === 'FLUID'"
        :multiple="true"
        :show-meds="false"
        :selected="state.check.ivInfusionLocationFluids || []"
        :case-conf="emitter.caseConf"
        :medications="medications"
        :disabled="readonly"
        :is-global="isGlobal"
        @select="
          (treatmentType, treatmentIds) => {
            updateIvInfusionLocationFluids(treatmentIds);
          }
        "
      />
      <help-tooltip v-if="locationFluidError" type="error">
        <span>{{ locationFluidError.message }}</span>
      </help-tooltip>
    </el-card>
    <migration-warning
      v-if="migrationWarning && state.check.fromInfusionMigration"
      :warning-object="migrationWarning"
      @dismiss="dismissMigrationWarning"
    />
  </div>
</template>

<script>
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import HelpTooltip from '~lib/common/components/HelpTooltip';
import MedicationFluidSelector from './common/MedicationFluidSelector';
import MigrationWarning from './common/MigrationWarning';
import InfusionBagVolumeSelect from '~lib/common/components/medications/InfusionBagVolumeSelect.vue';
import DosageCountCheck from './common/DosageCountCheck.vue';
import { unitTypes } from '~lib/common/config/medication';
import { fluids } from '~lib/common/config/fluids'
import IvLocationSelect from './common/IVLocationSelect.vue';
import infusionCheckMixins from './common/InfusionCheckMixins';

export default {
  name: 'infusion-check',
  components: {
    HelpTooltip,
    MigrationWarning,
    MedicationFluidSelector,
    InfusionBagVolumeSelect,
    DosageCountCheck,
    IvLocationSelect,
  },
  mixins: [ infusionCheckMixins ],
  props: [
    'medications',
    'readonly',
    'emitter',
    'ikey',
    'getData',
    'putData',
    'control',
  ],
  data() {
    const check = this.getData(this.ikey);
    return {
      state: {
        check: check || {
          id: null,
          target: 'CUMULATIVE',
          bagVolumes: [],
          count: null,
          countOperator: null,
          dosage: {
            min: null,
            max: null,
          },
          metric: 'GIVEN',
          unit: null,
          ivInfusionLocation: null,
          ivInfusionLocationIv: null,
          ivInfusionLocationMedications: null,
          ivInfusionLocationFluids: null,
        },
        selectedType:
          !check || check.type === 'INFUSION_MEDICATION'
            ? 'MEDICATION'
            : 'FLUID',
      },
    };
  },
  computed: {
    isGlobal() {
      return this.emitter.isGlobal;
    },
    selectedDosageForm() {
      const { id: medicationId } = this.state.check;
      if (this.isGlobal) {
        return this.medications.find(med => med._key === medicationId)
      }
      if (!this.emitter.caseConf) {
        return null;
      }
      return this.emitter.caseConf.dosageForms.find(
        caseDosageForm => caseDosageForm.medicationId === medicationId,
      );
    },
    selectedFluid() {
      if (this.isGlobal) {
        return fluids.find(f => f.key === fluidKey);
      }
      const { id: fluidKey } = this.state.check;
      if (!this.emitter.caseConf) {
        return null;
      }
      return this.emitter.caseConf.fluids.find(fluid => fluid.key === fluidKey);
    },
    selectedDosageFormInfusionRoute() {
      if (!this.selectedDosageForm) {
        return null;
      }
      return this.selectedDosageForm.enabledRoutes.find(
        route => route.key === 'IV_IO_INFUSION',
      );
    },
    availableBagVolumes() {
      if (this.state.selectedType === 'MEDICATION') {
        return this.selectedDosageFormInfusionRoute
          ? this.selectedDosageFormInfusionRoute.options.infusionBagVolumes
          : [];
      } else {
        return this.selectedFluid ? this.selectedFluid.infusionBagVolumes : [];
      }
    },
    availableUnits() {
      const availableCategories = [];
      if (this.state.selectedType === 'MEDICATION' && this.selectedDosageForm) {
        const { concentration } = this.selectedDosageForm;
        const numeratorUnitType = unitTypes.find(ut => ut.key === concentration.numeratorUnits);
        if (numeratorUnitType) {
          availableCategories.push(numeratorUnitType.category);
        }
        const denominatorUnitType = unitTypes.find(ut => ut.key === concentration.denominatorUnits);
        if (denominatorUnitType) {
          availableCategories.push(denominatorUnitType.category);
        }
      } else if (this.state.selectedType === 'FLUID') {
        availableCategories.push('VOLUME');
      }
      return unitTypes
        .filter(unitType => availableCategories.includes(unitType.category))
        .map(unitType => ({
          label: unitType.label.en,
          value: unitType.key,
        }));
    },
    countAvailable() {
      return this.state.check.target === 'CUMULATIVE' && this.state.check.metric === 'GIVEN';
    },
    dosageAvailable() {
      return this.state.check.target !== 'CUMULATIVE' || this.state.check.metric !== 'ANY';
    },
    warnings() {
      if (!this.emitter.$store) {
        return [];
      }

      const errors = get(
        this.emitter.$store.getters['validation/warningsByFieldAndId'],
        `caseLogicDescription.${this.control.parent.id}`,
        [],
      ).filter(error => error.metadata && error.metadata.scope === 'control');

      return uniqBy(errors, 'message');
    },
    migrationWarning() {
      return this.warnings.find(warning => warning.metadata.migrationWarning);
    }
  },
  watch: {
    dosageAvailable() {
      if (!this.dosageAvailable) {
        this.resetDosage();
        this.state.target = 'CUMULATIVE';
      }
    },
    countAvailable() {
      if (!this.countAvailable) {
        this.resetCount();
      }
    },
  },
  methods: {
    selectMedication(medicationId) {
      this.state.check.id = medicationId;
      this.state.selectedType = 'MEDICATION';
      this.resetBagVolumes();
      this.update();
    },
    selectFluid(fluidId) {
      this.state.check.id = fluidId;
      this.state.selectedType = 'FLUID';
      this.resetBagVolumes();
      this.update();
    },
    updateTarget(target) {
      this.state.check.target = target;
      if (target === 'CURRENT') {
        this.state.check.metric = 'DELIVERED';
      }
      this.update();
    },
    updateBagVolumes(bagVolumeKeys) {
      this.state.check.bagVolumes = bagVolumeKeys;
      this.update();
    },
    resetBagVolumes() {
      this.state.check.bagVolumes = [];
    },
    updateUnit(unit) {
      this.state.check.unit = unit;
      this.update();
    },
    updateDosage(dosage) {
      this.state.check.dosage = dosage;
      this.update();
    },
    resetDosage() {
      this.updateDosage({ min: null, max: null });
    },
    updateCount(count) {
      this.state.check.count = count;
      this.update();
    },
    resetCount() {
      this.state.check.count = null;
      this.state.check.countOperator = null;
      this.update();
    },
    updateCountOperator(op) {
      this.state.check.countOperator = op;
      this.update();
    },
    updateMetric(metric) {
      this.state.check.metric = metric;
      this.update();
    },
    updateIvInfusionLocation(location) {
      this.state.check.ivInfusionLocation = location;
      this.update();
    },
    updateIvInfusionLocationIv(ivLocation) {
      this.state.check.ivInfusionLocationIv = ivLocation;
      this.update();
    },
    updateIvInfusionLocationMedications(medicationIds) {
      this.state.check.ivInfusionLocationMedications = medicationIds;
      this.update();
    },
    updateIvInfusionLocationFluids(fluidIds) {
      this.state.check.ivInfusionLocationFluids = fluidIds;
      this.update();
    },
    dismissMigrationWarning() {
      this.state.check.fromInfusionMigration = null;
      this.update();
    },
    update() {
      const { check, selectedType } = this.state;
      this.putData(this.ikey, {
        ...check,
        type:
          selectedType === 'MEDICATION'
            ? 'INFUSION_MEDICATION'
            : 'INFUSION_FLUID',
      });
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>

<style scoped>
.el-select.error {
  border: solid 3px #f92c2d;
}

.medication-target-selects {
  display: flex;
  align-items: stretch;
}

.bag-volume-select-field {
  width: auto;
  margin-right: 0 !important;
}

.md-field >>> .md-select {
  height: 100%;
}

.md-field >>> .md-select input {
  height: 100%;
}

.el-select >>> .el-input {
  height: 100%;
}

.el-select >>> .el-input input {
  height: 100%;
}

.dosage-container {
  display: flex;
  flex-wrap: wrap;
}

.dosage-count-check {
  width: 600px;
}

.push-location-options-container {
  display: flex;
}

.el-card >>> .el-card__body {
  display: flex;
  align-items: stretch;
}
.iv-infusion-location-select {
  min-width: 100px;
}
.migration-warning {
  margin-top: 8px;
}

.help-tooltip {
  margin-left: -6px;
}
</style>
